/**
 * Platform fees service.
 *
 * Public read of the admin-managed commission rate and the fee mode
 * (who pays the commission: host, guest, or split 50/50). Cached in-memory
 * for 5 minutes so booking pages don't re-read Firestore on every render.
 */

import { useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from './firebase';

export const DEFAULT_COMMISSION_PERCENT = 10;
export const DEFAULT_FEE_MODE: FeeMode = 'host_pays';

export type FeeMode = 'host_pays' | 'guest_pays' | 'split';

export interface PlatformFees {
  commissionPercent: number;
  feeMode: FeeMode;
}

const DEFAULT_FEES: PlatformFees = {
  commissionPercent: DEFAULT_COMMISSION_PERCENT,
  feeMode: DEFAULT_FEE_MODE,
};

let cached: { fees: PlatformFees; loadedAt: number } | null = null;
let inflight: Promise<PlatformFees> | null = null;

const CACHE_TTL_MS = 5 * 60 * 1000;

function isFeeMode(v: unknown): v is FeeMode {
  return v === 'host_pays' || v === 'guest_pays' || v === 'split';
}

async function fetchFees(): Promise<PlatformFees> {
  try {
    const snap = await getDoc(doc(db, 'platformConfig', 'current'));
    if (!snap.exists()) return DEFAULT_FEES;
    const data = snap.data();
    const rawValue = data?.fees?.platformCommission?.value;
    const rate =
      typeof rawValue === 'number' && rawValue >= 0 && rawValue <= 100
        ? rawValue
        : DEFAULT_COMMISSION_PERCENT;
    const rawMode = data?.fees?.platformCommission?.mode ?? data?.fees?.feeMode;
    const mode: FeeMode = isFeeMode(rawMode) ? rawMode : DEFAULT_FEE_MODE;
    return { commissionPercent: rate, feeMode: mode };
  } catch (err) {
    console.warn('Failed to load platform fees, using defaults:', err);
    return DEFAULT_FEES;
  }
}

/**
 * Get the current platform fees. Cached for 5 minutes.
 */
export async function getPlatformFees(): Promise<PlatformFees> {
  if (cached && Date.now() - cached.loadedAt < CACHE_TTL_MS) {
    return cached.fees;
  }
  if (inflight) return inflight;

  inflight = fetchFees().then((fees) => {
    cached = { fees, loadedAt: Date.now() };
    inflight = null;
    return fees;
  });
  return inflight;
}

/**
 * Backward-compat helper: just the commission percentage.
 */
export async function getPlatformCommissionRate(): Promise<number> {
  const fees = await getPlatformFees();
  return fees.commissionPercent;
}

/**
 * React hook: returns the current platform fees (rate + mode).
 * Defaults until the live value loads.
 */
export function usePlatformFees(): PlatformFees {
  const [fees, setFees] = useState<PlatformFees>(cached?.fees ?? DEFAULT_FEES);

  useEffect(() => {
    let alive = true;
    getPlatformFees().then((f) => {
      if (alive) setFees(f);
    });
    return () => {
      alive = false;
    };
  }, []);

  return fees;
}

/**
 * Backward-compat: just the rate as a percentage number.
 */
export function usePlatformCommissionRate(): number {
  return usePlatformFees().commissionPercent;
}

// --- Pricing math ---

export interface CommissionBreakdown {
  guestSurcharge: number; // amount added to subtotal that the guest pays
  guestTotal: number; // what the guest actually pays
  hostPayout: number; // what credits the host wallet on checkout
  platformCommission: number; // what the platform takes
  feeMode: FeeMode;
  commissionPercent: number;
}

/**
 * Compute the commission breakdown for a given subtotal under the given mode.
 * `subtotal` is the listing's nightly+cleaning total (no fees included).
 * Rounds money to 2dp.
 */
export function computeCommissionBreakdown(
  subtotal: number,
  fees: PlatformFees
): CommissionBreakdown {
  const round2 = (n: number) => Math.round(n * 100) / 100;
  const r = (fees.commissionPercent || 0) / 100;
  const platformCommission = round2(subtotal * r);

  let guestSurcharge = 0;
  let hostPayout = 0;

  switch (fees.feeMode) {
    case 'guest_pays':
      // Guest pays subtotal + commission. Host gets the full subtotal.
      guestSurcharge = platformCommission;
      hostPayout = round2(subtotal);
      break;
    case 'split':
      // Split 50/50: guest pays half on top, host absorbs half off the bottom.
      guestSurcharge = round2(platformCommission / 2);
      hostPayout = round2(subtotal - (platformCommission - guestSurcharge));
      break;
    case 'host_pays':
    default:
      // Guest pays subtotal only. Host absorbs the full commission.
      guestSurcharge = 0;
      hostPayout = round2(subtotal - platformCommission);
      break;
  }

  const guestTotal = round2(subtotal + guestSurcharge);

  return {
    guestSurcharge,
    guestTotal,
    hostPayout,
    platformCommission,
    feeMode: fees.feeMode,
    commissionPercent: fees.commissionPercent,
  };
}
